import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { RadioButton } from "primereact/radiobutton";
import { REIButton, REICheckBox, REIDialoge, REIDropDown } from "../../components/ui";
import { IMemberProfile } from "../../interface/analysed";
import { useFilterSelection } from "./FilterSelectionContext";

export const Filters: React.FC<{
  tab?: "MyEquity" | "MyCashFlow" | "MyTracker";
}> = ({ tab }) => {
  const {
    selectedProfiles,
    setSelectedProfiles,
    selectedEntityId,
    setSelectedEntityId,
    isShowingTotals,
    setIsShowingTotals,
  } = useFilterSelection();
  const userProfile = useSelector((state: RootState) => state.myTracker.profileData);
  const memberEntities = useSelector((state: RootState) => state.myTracker.memberEntities);

  const [showEntityDialog, setShowEntityDialog] = useState(false);
  const [selectedEquityOption, setSelectedEquityOption] = useState(isShowingTotals ? "totals" : "portions");
  const [entityData, setEntityData] = useState<{ entityName: string; value: number }[]>([]);

  const [tempSelectedFilter, setTempSelectedFilter] = useState<"entity" | "profile">("entity");
  const [tempSelectedEntityId, setTempSelectedEntityId] = useState<number>(0);
  const [tempSelectedProfiles, setTempSelectedProfiles] = useState<IMemberProfile[]>([]);

  useEffect(() => {
    localStorage.setItem("selectedProfiles", JSON.stringify(selectedProfiles));
  }, [selectedProfiles]);

  useEffect(() => {
    localStorage.setItem("selectedEntityId", JSON.stringify(selectedEntityId));
  }, [selectedEntityId]);

  useEffect(() => {
    setTempSelectedEntityId(() => selectedEntityId);
  }, [selectedEntityId]);

  useEffect(() => {
    const entities = [
      { entityName: "All entities", value: 0 },
      ...memberEntities.map((e) => ({ entityName: e.EntityName, value: e.MemberEntityId })),
    ];
    setEntityData(entities);
  }, [memberEntities]);

  useEffect(() => {
    if (selectedEntityId !== 0) {
      setTempSelectedEntityId(selectedEntityId);
      setTempSelectedProfiles([]);
      setTempSelectedFilter("entity");
    } else {
      setTempSelectedFilter("profile");
    }
  }, [selectedEntityId]);

  useEffect(() => {
    if (selectedProfiles.length > 0) {
      setTempSelectedProfiles(selectedProfiles);
      setTempSelectedFilter("profile");
    } else {
      setTempSelectedFilter("entity");
    }
  }, [selectedProfiles]);

  const openDialog = () => {
    setTempSelectedEntityId(selectedEntityId);
    setTempSelectedProfiles([...selectedProfiles]);
    setShowEntityDialog(true);
  };

  const applyFilters = () => {
    if (tempSelectedFilter === "entity") {
      setSelectedEntityId(tempSelectedEntityId);
      localStorage.setItem("selectedEntityId", tempSelectedEntityId.toString());
      localStorage.setItem("lastSelectedFilter", "entity");
      setSelectedProfiles([]);
    } else {
      setSelectedProfiles(tempSelectedProfiles);
      localStorage.setItem("selectedProfiles", JSON.stringify(tempSelectedProfiles));
      localStorage.setItem("lastSelectedFilter", "profile");

      setSelectedEntityId(0);
    }
    setShowEntityDialog(false);
  };

  const cancelFilters = () => {
    setShowEntityDialog(false);
    localStorage.getItem("lastSelectedFilter") === "entity"
      ? setTempSelectedFilter(() => "entity")
      : setTempSelectedFilter(() => "profile");
  };

  const handleTempProfileToggle = (profile: IMemberProfile) => {
    const exists = tempSelectedProfiles.find((p) => p.MemberProfileId === profile.MemberProfileId);
    if (exists) {
      setTempSelectedProfiles(tempSelectedProfiles.filter((p) => p.MemberProfileId !== profile.MemberProfileId));
    } else {
      setTempSelectedProfiles([...tempSelectedProfiles, profile]);
    }
  };

  const renderDialogContent = () => (
    <div className="flex flex-col gap-3">
      <label>You can view your summary statement from the point of view of:</label>
      <div className="flex items-center gap-2">
        <RadioButton
          inputId="entity"
          data-testid="ptflt-rdo-entity"
          name="filter_type"
          value="entity"
          checked={tempSelectedFilter === "entity"}
          onChange={() => {
            setTempSelectedFilter("entity");
          }}
        />
        <label htmlFor="entity">Entity:</label>
        <REIDropDown
          id={"ptflt-drop-entity"}
          data={entityData}
          label="entityName"
          value={tempSelectedEntityId}
          onSelectionChange={(e) => {
            setTempSelectedEntityId(e.value);
            setTempSelectedProfiles([]);
            setTempSelectedFilter("entity");
          }}
          isDisabled={tempSelectedFilter === "profile"}
        />
      </div>
      <div className="flex gap-2">
        <RadioButton
          inputId="profile"
          data-testid="ptflt-rdo-profile"
          name="filter_type"
          value="profile"
          checked={tempSelectedFilter === "profile"}
          onChange={() => setTempSelectedFilter("profile")}
        />
        <label htmlFor="profile">Profile(s):</label>
        <div className="flex flex-col gap-1">
          {userProfile.map((profile) => (
            <div key={profile.MemberProfileId} className="flex items-center gap-2">
              <REICheckBox
                id={`ptflt-chk-profile-${profile.MemberProfileId}`}
                label=""
                disabled={tempSelectedFilter === "entity"}
                checked={tempSelectedProfiles.some((p) => p.MemberProfileId === profile.MemberProfileId)}
                onChange={() => handleTempProfileToggle(profile)}
              />
              <span>{profile.MemberName}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <REIButton id={"ptflt-btn-cancel"} label="Cancel" onClick={cancelFilters} />
        <REIButton id={"ptflt-btn-ok"} label="OK" onClick={applyFilters} />
      </div>
    </div>
  );

  const getSelectedEntityName = () => {
    let prefix = "Summary for";
    switch (tab) {
      case "MyCashFlow":
        prefix = "Cashflow summary for";
        break;
      case "MyEquity":
      case "MyTracker":
        prefix = "Equity summary for";
        break;
      default:
        prefix = "Summary for";
    }
    if (selectedEntityId === 0) {
      return `${prefix} all entities`;
    }
    const entityName = entityData.find((e) => e.value === selectedEntityId)?.entityName ?? "";
    return `${prefix} entity: ${entityName}`;
  };

  return (
    <>
      <REIDialoge
        contentComponent={renderDialogContent()}
        visible={showEntityDialog}
        handleEditClick={() => {
          setShowEntityDialog(false);
        }}
        header="Filter Selection"
        showHeader={true}
      />
      <div className="flex flex-col gap-2">
        {tempSelectedFilter === "entity" && (
          <div className="flex gap-7 items-center font-bold text-lg">
            <span>{getSelectedEntityName()}</span>

            <REIButton
              id={"ptflt-btn-changeentity"}
              label="Change"
              onClick={() => {
                openDialog();
                setTempSelectedFilter("entity");
              }}
            />
          </div>
        )}
        {tempSelectedFilter === "entity" && (
          <div className="flex items-center gap-3">
            <label>Showing:</label>
            <RadioButton
              inputId="totals"
              data-testid="ptflt-rdo-totals"
              name="portion_view"
              value="totals"
              checked={selectedEquityOption === "totals"}
              onChange={() => {
                setSelectedEquityOption("totals");
                setIsShowingTotals(true);
              }}
            />
            <label htmlFor="totals">Totals</label>

            <RadioButton
              inputId="portions"
              data-testid="ptflt-rdo-portions"
              name="portion_view"
              value="portions"
              checked={selectedEquityOption === "portions"}
              onChange={() => {
                setSelectedEquityOption("portions");
                setIsShowingTotals(false);
              }}
            />
            <label htmlFor="portions">This entity's portion</label>
          </div>
        )}
        {tempSelectedFilter === "profile" && (
          <div className="flex flex-row gap-2 justify-around">
            <div className="flex flex-row gap-2">
              <div className="text-xl font-bold"> Equity summary for profiles:</div>
              <div className="flex flex-col align-middle text-lg ">
                {" "}
                {selectedProfiles.map((item) => (
                  <div className="font-bold"> {item.MemberName}</div>
                ))}
              </div>
            </div>
            <span>
              {" "}
              <REIButton
                id={"ptflt-btn-changeprofile"}
                label="Change"
                onClick={() => {
                  openDialog();
                  setTempSelectedFilter("profile");
                }}
              />
            </span>
          </div>
        )}
      </div>
    </>
  );
};
